import React, { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Treemap,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  MAJOR_COINS_CHART_OPTIONS,
  type MajorCoinsChartType,
  normalizeMajorCoinsChartType,
} from '../../constants/majorCoinsChartTypes';
import type { MajorCoinsMarketSnapshot } from '../../services/publicMarketService';

const COIN_COLORS = [
  '#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#db2777',
  '#0891b2', '#4f46e5', '#16a34a', '#ca8a04', '#64748b',
];

const STORAGE_KEY = 'majorCoinsChartType';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpoint}px)`).matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);

  return isMobile;
}

function chartAreaHeight(type: MajorCoinsChartType, rowCount: number, isMobile: boolean) {
  switch (type) {
    case 'bars':
      return Math.max(isMobile ? 320 : 280, rowCount * 34 + 32);
    case 'donut':
      return isMobile ? 220 + rowCount * 28 + 24 : 280;
    case 'treemap':
    case 'columns':
    case 'area':
    default:
      return isMobile ? 260 : 280;
  }
}

function fmtMarketCap(n: number, estimated?: boolean) {
  if (!n || n <= 0) return estimated ? 'Est.' : '—';
  if (n >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(2)}T`;
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(0)}M`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtPrice(n: number) {
  if (n >= 1000) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}

function readStoredChartType(fallback: MajorCoinsChartType, allowStored: boolean): MajorCoinsChartType {
  if (!allowStored) return fallback;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return normalizeMajorCoinsChartType(saved);
  } catch {
    /* ignore */
  }
  return fallback;
}

function ShareTooltip({
  active,
  payload,
  estimated,
}: {
  active?: boolean;
  payload?: Array<{ payload?: { symbol?: string; share?: number; marketCap?: number } }>;
  estimated?: boolean;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row?.symbol) return null;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '8px 12px',
      fontSize: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>{row.symbol}</div>
      <div style={{ color: '#64748b' }}>
        {(row.share ?? 0).toFixed(1)}% share · {fmtMarketCap(row.marketCap ?? 0, estimated)}
      </div>
    </div>
  );
}

function TreemapTile(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  index?: number;
  share?: number;
}) {
  const { x = 0, y = 0, width = 0, height = 0, name, index = 0, share } = props;
  if (width < 4 || height < 4) return null;
  const fill = COIN_COLORS[index % COIN_COLORS.length];
  const showLabel = width > 36 && height > 28;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
        rx={4}
      />
      {showLabel && (
        <>
          <text x={x + width / 2} y={y + height / 2 - 4} textAnchor="middle" fill="#fff" fontSize={12} fontWeight={700}>
            {name}
          </text>
          <text x={x + width / 2} y={y + height / 2 + 12} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize={10}>
            {(share ?? 0).toFixed(1)}%
          </text>
        </>
      )}
    </g>
  );
}

interface MajorCoinsMarketPanelProps {
  data: MajorCoinsMarketSnapshot;
  masked?: boolean;
  showChartStyleOptions?: boolean;
  defaultChartType?: string;
}

const MajorCoinsMarketPanel: React.FC<MajorCoinsMarketPanelProps> = ({
  data,
  masked,
  showChartStyleOptions = true,
  defaultChartType,
}) => {
  const adminDefault = normalizeMajorCoinsChartType(defaultChartType);
  const [chartType, setChartType] = useState<MajorCoinsChartType>(() =>
    readStoredChartType(adminDefault, showChartStyleOptions),
  );
  const estimated = data.source === 'kucoin_estimate';
  const isMobile = useIsMobile();

  const chartData = data.coins.map((c, i) => ({
    symbol: c.symbol,
    name: c.name,
    share: c.sharePercent,
    marketCap: c.marketCap,
    change24h: c.change24h,
    fill: COIN_COLORS[i % COIN_COLORS.length],
  }));

  const totalShare = chartData.reduce((sum, c) => sum + c.share, 0);
  const totalCap = chartData.reduce((sum, c) => sum + c.marketCap, 0);
  const chartHeight = chartAreaHeight(chartType, chartData.length, isMobile);

  useEffect(() => {
    if (!showChartStyleOptions) {
      setChartType(adminDefault);
      return;
    }
    setChartType(readStoredChartType(adminDefault, true));
  }, [adminDefault, showChartStyleOptions]);

  const selectChart = (id: MajorCoinsChartType) => {
    setChartType(id);
    if (!showChartStyleOptions) return;
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* ignore */
    }
  };

  const renderChart = () => {
    const tooltip = <Tooltip content={<ShareTooltip estimated={estimated} />} />;

    switch (chartType) {
      case 'donut':
        return (
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: 16,
            width: '100%',
          }}>
            <div style={{
              flex: isMobile ? '0 0 auto' : '1 1 220px',
              width: '100%',
              maxWidth: isMobile ? 280 : undefined,
              margin: isMobile ? '0 auto' : undefined,
              height: isMobile ? 200 : 240,
              position: 'relative',
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="share"
                    nameKey="symbol"
                    cx="50%"
                    cy="50%"
                    innerRadius="52%"
                    outerRadius="88%"
                    paddingAngle={2}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {chartData.map((entry) => (
                      <Cell key={entry.symbol} fill={entry.fill} />
                    ))}
                  </Pie>
                  {tooltip}
                </PieChart>
              </ResponsiveContainer>
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Top 10
                </span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>
                  {totalShare.toFixed(0)}%
                </span>
                <span style={{ fontSize: 11, color: '#64748b' }}>of majors basket</span>
              </div>
            </div>
            <div style={{
              flex: '1 1 auto',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr',
              gap: 8,
              width: '100%',
              minWidth: 0,
            }}>
              {chartData.map((c) => (
                <div key={c.symbol} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, minWidth: 0 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: c.fill, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#334155', width: 40, flexShrink: 0 }}>{c.symbol}</span>
                  <div style={{ flex: 1, minWidth: 0, height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, c.share * 2)}%`, height: '100%', background: c.fill, borderRadius: 99 }} />
                  </div>
                  <span style={{ color: '#64748b', width: 44, textAlign: 'right', flexShrink: 0 }}>{c.share.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'bars':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
            >
              <XAxis type="number" unit="%" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis
                type="category"
                dataKey="symbol"
                width={48}
                tick={{ fontSize: 12, fontWeight: 700, fill: '#334155' }}
              />
              {tooltip}
              <Bar dataKey="share" radius={[0, 6, 6, 0]} maxBarSize={22}>
                {chartData.map((entry) => (
                  <Cell key={entry.symbol} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'treemap':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={chartData}
              dataKey="marketCap"
              nameKey="symbol"
              stroke="#fff"
              content={<TreemapTile />}
            >
              {tooltip}
            </Treemap>
          </ResponsiveContainer>
        );

      case 'columns':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="symbol"
                tick={{ fontSize: 11, fontWeight: 700, fill: '#334155' }}
                axisLine={{ stroke: '#cbd5e1' }}
                tickLine={false}
              />
              <YAxis unit="%" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={42} />
              {tooltip}
              <Bar dataKey="share" radius={[6, 6, 0, 0]} maxBarSize={40}>
                {chartData.map((entry) => (
                  <Cell key={entry.symbol} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case 'area':
      default:
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="majorCoinsShareGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="symbol"
                tick={{ fontSize: 12, fontWeight: 700, fill: '#334155' }}
                axisLine={{ stroke: '#cbd5e1' }}
                tickLine={false}
              />
              <YAxis unit="%" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={42} />
              {tooltip}
              <Area
                type="monotone"
                dataKey="share"
                stroke="#2563eb"
                strokeWidth={2.5}
                fill="url(#majorCoinsShareGradient)"
                dot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: '#2563eb' }}
                activeDot={{ r: 6 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '20px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#2563eb', flexShrink: 0 }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', letterSpacing: 1, textTransform: 'uppercase' }}>
              Major Crypto Market Cap
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            Top 10 majors — combined {fmtMarketCap(totalCap, estimated)}
            {estimated && !masked ? ' (estimated from KuCoin volume)' : ''}
          </p>
        </div>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          Updated {timeAgo(data.updatedAt)}
        </span>
      </div>

      <div style={{
        filter: masked ? 'blur(6px)' : undefined,
        userSelect: masked ? 'none' : undefined,
      }}>
        {chartData.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            Market data loading…
          </div>
        ) : (
          <>
            {showChartStyleOptions && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Chart style
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {MAJOR_COINS_CHART_OPTIONS.map((opt) => {
                    const active = chartType === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => selectChart(opt.id)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 999,
                          border: `1.5px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                          background: active ? '#eff6ff' : '#fff',
                          color: active ? '#1d4ed8' : '#475569',
                          fontSize: 12,
                          fontWeight: active ? 700 : 600,
                          cursor: 'pointer',
                          lineHeight: 1.2,
                        }}
                      >
                        {opt.label}
                        <span style={{ display: 'block', fontSize: 10, fontWeight: 500, color: active ? '#3b82f6' : '#94a3b8', marginTop: 2 }}>
                          {opt.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{
              width: '100%',
              height: chartType === 'donut' ? 'auto' : chartHeight,
              minHeight: chartType === 'donut' ? undefined : chartHeight,
              marginBottom: 20,
              overflow: 'visible',
            }}>
              {renderChart()}
            </div>

            <div style={{ overflowX: 'auto', marginTop: 4, clear: 'both' }}>
              <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                    {['Coin', 'Price', '24h', estimated ? 'Est. weight' : 'Market cap', 'Share'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '8px 10px',
                          textAlign: h === 'Coin' ? 'left' : 'right',
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.coins.map((c, i) => (
                    <tr key={c.symbol} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '10px', fontWeight: 700, color: '#1e293b' }}>
                        {c.symbol}
                        {!isMobile && (
                          <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6, fontSize: 12 }}>{c.name}</span>
                        )}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#334155' }}>{fmtPrice(c.price)}</td>
                      <td style={{
                        padding: '10px',
                        textAlign: 'right',
                        fontWeight: 600,
                        color: c.change24h >= 0 ? '#15803d' : '#b91c1c',
                      }}>
                        {c.change24h >= 0 ? '+' : ''}{c.change24h.toFixed(2)}%
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#334155' }}>
                        {fmtMarketCap(c.marketCap, estimated)}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                        {c.sharePercent.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function timeAgo(ts: number) {
  const sec = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

export default MajorCoinsMarketPanel;
